import {
    EditorRowCapabilities,
    GetRowCapabilities,
} from "../editorCapabilities";
import { NestedEditorRow } from "./nestedRowTypes";

export type NestedEditorHost = "workspace" | "template";

// No "enable" gesture: the chart page enables an archived account on
// `isDisabled` alone, so callers gate the archive direction only.
export type NestedRowGesture = "archive" | "delete" | "merge";

export interface NestedRowRefusal {
    entityId: number;
    reason: string;
}

export interface NestedRowRefusalGroup {
    reason: string;
    entityIds: number[];
}

// One entry per distinct reason, in the refusals' order, naming every business
// that gave it. `resolveNestedRowRefusal` keeps one reason per business, so a
// business lands in exactly one group.
export const groupNestedRowRefusals = (
    refusals: NestedRowRefusal[],
): NestedRowRefusalGroup[] => {
    const entityIdsByReason = new Map<string, number[]>();
    for (const refusal of refusals) {
        const entityIds = entityIdsByReason.get(refusal.reason);
        if (entityIds) {
            entityIds.push(refusal.entityId);
            continue;
        }
        entityIdsByReason.set(refusal.reason, [refusal.entityId]);
    }
    return [...entityIdsByReason].map(([reason, entityIds]) => ({
        reason,
        entityIds,
    }));
};

// Plain-text rendering of the same grouping, for the toast, which cannot carry
// the entity icons the ⋯ tooltip draws.
export const formatNestedRowRefusalsMessage = (
    refusals: NestedRowRefusal[],
    businessNamesFor?: (entityIds: number[]) => string[],
): string =>
    groupNestedRowRefusals(refusals)
        .map((group) => {
            const businessNames = businessNamesFor?.(group.entityIds) ?? [];
            return businessNames.length === 0
                ? group.reason
                : `${group.reason}: ${businessNames.join(", ")}`;
        })
        .join("\n");

const resolveGestureReason = (
    capabilities: EditorRowCapabilities,
    gesture: NestedRowGesture,
): string | null => {
    if (gesture === "archive") {
        return capabilities.archiveBlockReason;
    }
    if (gesture === "delete") {
        return capabilities.deleteBlockReason;
    }
    return capabilities.mergeBlockReason;
};

// Every unique refusing business behind the row, first reason per business, in
// constituent order. Several refusing constituents in one business collapse to
// one entry. Empty array means the row is not refused.
export const resolveNestedRowRefusal = ({
    row,
    gesture,
    getRowCapabilities,
}: {
    row: NestedEditorRow;
    gesture: NestedRowGesture;
    getRowCapabilities: GetRowCapabilities;
}): NestedRowRefusal[] => {
    const refusals: NestedRowRefusal[] = [];
    const seenEntityIds = new Set<number>();
    for (const account of row.accounts) {
        if (seenEntityIds.has(account.entityId)) {
            continue;
        }
        const capabilities = getRowCapabilities(account);
        const reason = resolveGestureReason(capabilities, gesture);
        if (reason != null) {
            seenEntityIds.add(account.entityId);
            refusals.push({ entityId: account.entityId, reason });
        }
    }
    return refusals;
};
